import { AnimatePresence } from 'framer-motion';
import moment from 'moment';
import { Fragment, PureComponent, ReactNode } from 'react';
import fetchAllMedicalRecordsInADoc, {
    FilesEntity,
    SmartReport,
} from '../api/fetchAllMedicalRecordsInADoc';
import AppLoader from '../components/common/AppLoader';
import RightPopUp from '../components/common/RightPopUp';
import { LabVitalEntity } from '../store/types/prescription';
import { track } from '../tracking';
import { UIDocumentEntity } from './MedicalRecordsRightPanel';
import { ImproveSmartReportDialog } from '../components/common/ImproveSmartReportDialog';
import { WhatIsSmartReportDialog } from '../components/common/WhatIsSmartReportDialog';
import { notification } from 'antd';
import { captureError } from './captureError';
import MediaViewerNew from '../components/common/MediaViewerNew';
import { OPTIONS } from '../components/common/MedicalRecordFileTypeSelector';

type Props = {
    documentFolder: UIDocumentEntity;
    onClick?: () => void;
    onDelete?: () => void;
    copyToPrescription?: (items: LabVitalEntity[]) => void;
    containerCss?: string;
    showPtName?: boolean;
    isSelected?: boolean;
    hideActionCTAs?: boolean;
};

type State = {
    isPopUpVisible?: boolean;
    isFilesInDocLoading?: boolean;
    files?: FilesEntity[] | null;
    smartReport?: SmartReport | null;
    improveSmartReportDialog?: boolean;
    whatIsSmartReportDialog?: boolean;
    notesCount: number;
};

class MedicalRecord extends PureComponent<Props, State> {
    state: State = {
        isPopUpVisible: false,
        isFilesInDocLoading: false,
        files: null,
        smartReport: null,
        improveSmartReportDialog: false,
        whatIsSmartReportDialog: false,
        notesCount: 0,
    };

    componentDidMount(): void {
        const { isSelected, documentFolder } = this.props;
        this.onSetNotesCount(documentFolder?.notes || 0);
        if (isSelected) this.onSelect();
    }

    onSelect = (): void => {
        const { onClick, documentFolder } = this.props;

        this.setState({
            isFilesInDocLoading: true,
            isPopUpVisible: true,
        });

        if ('files' in documentFolder) {
            this.setState({
                files: documentFolder.files,
                smartReport: undefined,
                isFilesInDocLoading: false,
            });
        } else {
            fetchAllMedicalRecordsInADoc(documentFolder._pt_oid, documentFolder.document_id)
                .then((res) => {
                    const f = res.files;
                    this.setState({
                        files: f,
                        smartReport: res.smart_report,
                        isFilesInDocLoading: false,
                    });
                })
                .catch((e) => {
                    console.error(e);
                    captureError(e);
                    notification.error({
                        message: 'Failed to fetch medical files',
                        duration: 5,
                        placement: 'topRight',
                    });

                    track('ERROR', {
                        on: 'MedicalViewer#onRecordSelect',
                        what: 'fetchAllMedicalRecordsInADoc',
                        error: e.message,
                    });
                });
        }

        if (onClick) {
            onClick();
        }
    };

    closeMediaViewer = async (): Promise<void> => {
        this.setState({ isPopUpVisible: false });
    };

    improveSmartReportDialogToggler = (): void => {
        track('TOGGLE_DIALOG', {
            while: 'improve-smart-report-dialog',
            value: (!this.state.whatIsSmartReportDialog).toString(),
        });
        this.setState({ improveSmartReportDialog: !this.state.improveSmartReportDialog });
    };

    whatIsSmartReportDialogToggler = (): void => {
        track('TOGGLE_DIALOG', {
            while: 'what-is-smart-report-dialog',
            value: (!this.state.whatIsSmartReportDialog).toString(),
        });
        this.setState({ whatIsSmartReportDialog: !this.state.whatIsSmartReportDialog });
    };

    onCopyToPrescription = this.props.copyToPrescription
        ? (items: LabVitalEntity[]): void => {
              const { documentFolder, copyToPrescription } = this.props;
              const date = moment(
                  documentFolder?.document_date ||
                      (documentFolder?.u_at ? documentFolder?.u_at * 1000 : undefined) ||
                      new Date(),
              ).toISOString();
              copyToPrescription && copyToPrescription(items.map((t) => ({ ...t, date })));
          }
        : undefined;

    onSetNotesCount = (count: number) => {
        this.setState({
            notesCount: count,
        });
    };

    render(): ReactNode {
        const {
            documentFolder,
            onDelete,
            containerCss,
            showPtName,
            isSelected,
            hideActionCTAs,
        } = this.props;
        const {
            isPopUpVisible,
            files,
            smartReport,
            isFilesInDocLoading,
            improveSmartReportDialog,
            whatIsSmartReportDialog,
            notesCount,
        } = this.state;

        const rtTag = documentFolder?.user_tags?.find((tag) => tag?.startsWith('RT#'));

        return (
            <Fragment>
                <AnimatePresence>
                    {isPopUpVisible && (
                        <RightPopUp closeMe={this.closeMediaViewer}>
                            <Fragment>
                                <div
                                    className="flex items-center justify-between px-16 py-12 bg-darwin-neutral-50"
                                    style={{ height: 60 }}
                                >
                                    <div className="flex items-center gap-12">
                                        <div
                                            onClick={this.closeMediaViewer}
                                            className="flex items-center justify-center w-16 h-16 cursor-pointer"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 448 512"
                                                className="fill-darwin-neutral-1000 w-16 h-16"
                                            >
                                                <path d="M447.1 256c0 13.25-10.76 24.01-24.01 24.01H83.9l132.7 126.6c9.625 9.156 9.969 24.41 .8125 33.94c-9.156 9.594-24.34 9.938-33.94 .8125l-176-168C2.695 268.9 .0078 262.6 .0078 256S2.695 243.2 7.445 238.6l176-168C193 61.51 208.2 61.85 217.4 71.45c9.156 9.5 8.812 24.75-.8125 33.94l-132.7 126.6h340.1C437.2 232 447.1 242.8 447.1 256z" />
                                            </svg>
                                        </div>
                                        {documentFolder.is_smart_report_available && (
                                            <p className="font-400 text-20">
                                                Smart report for record
                                            </p>
                                        )}
                                        <div className="rounded-t-4 text-neutral-black font-700 "></div>
                                    </div>
                                    <div className="flex items-center space-x-8 ">
                                        {documentFolder.is_smart_report_available && (
                                            <div
                                                className="flex items-center space-x-8 bg-white rounded-4 px-8 py-8 border border-darwin-primary-500 cursor-pointer"
                                                onClick={this.whatIsSmartReportDialogToggler}
                                            >
                                                <div className="flex items-center justify-center w-16 h-16">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        viewBox="0 0 512 512"
                                                        className="fill-darwin-accent-bright-blue-800 w-12 h-12"
                                                    >
                                                        <path d="M327.5 85.19L384 64L405.2 7.491C406.9 2.985 411.2 0 416 0C420.8 0 425.1 2.985 426.8 7.491L448 64L504.5 85.19C509 86.88 512 91.19 512 96C512 100.8 509 105.1 504.5 106.8L448 128L426.8 184.5C425.1 189 420.8 192 416 192C411.2 192 406.9 189 405.2 184.5L384 128L327.5 106.8C322.1 105.1 320 100.8 320 96C320 91.19 322.1 86.88 327.5 85.19V85.19zM176 73.29C178.6 67.63 184.3 64 190.6 64C196.8 64 202.5 67.63 205.1 73.29L257.8 187.3L371.8 240C377.5 242.6 381.1 248.3 381.1 254.6C381.1 260.8 377.5 266.5 371.8 269.1L257.8 321.8L205.1 435.8C202.5 441.5 196.8 445.1 190.6 445.1C184.3 445.1 178.6 441.5 176 435.8L123.3 321.8L9.292 269.1C3.627 266.5 0 260.8 0 254.6C0 248.3 3.627 242.6 9.292 240L123.3 187.3L176 73.29zM166.9 207.5C162.1 217.8 153.8 226.1 143.5 230.9L92.32 254.6L143.5 278.2C153.8 282.1 162.1 291.3 166.9 301.6L190.6 352.8L214.2 301.6C218.1 291.3 227.3 282.1 237.6 278.2L288.8 254.6L237.6 230.9C227.3 226.1 218.1 217.8 214.2 207.5L190.6 156.3L166.9 207.5zM405.2 327.5C406.9 322.1 411.2 320 416 320C420.8 320 425.1 322.1 426.8 327.5L448 384L504.5 405.2C509 406.9 512 411.2 512 416C512 420.8 509 425.1 504.5 426.8L448 448L426.8 504.5C425.1 509 420.8 512 416 512C411.2 512 406.9 509 405.2 504.5L384 448L327.5 426.8C322.1 425.1 320 420.8 320 416C320 411.2 322.1 406.9 327.5 405.2L384 384L405.2 327.5z"></path>
                                                    </svg>
                                                </div>
                                                <p className="caption-1 bold text-darwin-accent-bright-blue-800">
                                                    what is a smart report?
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {!files || isFilesInDocLoading ? (
                                    <div className="flex flex-1 flex-auto w-full h-full items-center justify-center">
                                        <AppLoader />
                                    </div>
                                ) : (
                                    <div className="flex w-full h-full overflow-hidden">
                                        <MediaViewerNew
                                            files={files || []}
                                            smartReport={smartReport || undefined}
                                            copyToPrescription={this.onCopyToPrescription}
                                            smartReportHandler={
                                                this.improveSmartReportDialogToggler
                                            }
                                            documentFolder={documentFolder}
                                            notesCount={notesCount}
                                            onSetNotesCount={this.onSetNotesCount}
                                            patientId={documentFolder._pt_oid}
                                        />
                                    </div>
                                )}
                                <AnimatePresence>
                                    {improveSmartReportDialog && (
                                        <ImproveSmartReportDialog
                                            closeMe={this.improveSmartReportDialogToggler}
                                        />
                                    )}
                                </AnimatePresence>
                                <AnimatePresence>
                                    {whatIsSmartReportDialog && (
                                        <WhatIsSmartReportDialog
                                            closeMe={this.whatIsSmartReportDialogToggler}
                                        />
                                    )}
                                </AnimatePresence>
                            </Fragment>
                        </RightPopUp>
                    )}
                </AnimatePresence>

                <div
                    key={documentFolder.document_id}
                    onClick={this.onSelect}
                    className={`rounded-4 cursor-pointer bg-white ${containerCss} ${
                        isSelected ? 'border border-darwin-accent-bright-blue-800' : ''
                    }`}
                    style={{ boxShadow: '0px 1px 2px #c6c6c6' }}
                >
                    <div className="relative flex items-center justify-center flex-col rounded-t-4 bg-white h-200">
                        <img
                            src={
                                documentFolder.is_processing
                                    ? '/images/mr-analysing.png'
                                    : documentFolder.thumbnail
                            }
                            style={{ objectFit: 'contain' }}
                            className={
                                !documentFolder.is_processing
                                    ? 'w-full h-full rounded-t-4'
                                    : 'w-84 h-92 mt-32'
                            }
                        />

                        <div className="absolute top-8 left-12 space-x-10 flex items-center">
                            {documentFolder.is_processing && (
                                <div className="flex items-center space-x-8 bg-darwin-neutral-50 rounded-4 px-8 py-4">
                                    <div className="flex items-center justify-center w-16 h-16">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 576 512"
                                            className="fill-darwin-accent-bright-blue-800 w-12 h-12 animate-spin"
                                        >
                                            <path d="M312 104C312 117.3 301.3 128 288 128C274.7 128 264 117.3 264 104V24C264 10.75 274.7 0 288 0C301.3 0 312 10.75 312 24V104zM312 488C312 501.3 301.3 512 288 512C274.7 512 264 501.3 264 488V408C264 394.7 274.7 384 288 384C301.3 384 312 394.7 312 408V488zM32 256C32 242.7 42.75 232 56 232H136C149.3 232 160 242.7 160 256C160 269.3 149.3 280 136 280H56C42.75 280 32 269.3 32 256zM520 232C533.3 232 544 242.7 544 256C544 269.3 533.3 280 520 280H440C426.7 280 416 269.3 416 256C416 242.7 426.7 232 440 232H520zM469 74.98C478.4 84.35 478.4 99.55 469 108.9L412.5 165.5C403.1 174.9 387.9 174.9 378.5 165.5C369.1 156.1 369.1 140.9 378.5 131.6L435.1 74.98C444.5 65.61 459.6 65.61 469 74.98zM140.9 437C131.5 446.4 116.4 446.4 106.1 437C97.61 427.6 97.61 412.4 106.1 403.1L163.5 346.5C172.9 337.1 188.1 337.1 197.5 346.5C206.9 355.9 206.9 371.1 197.5 380.4L140.9 437zM106.1 74.98C116.4 65.61 131.5 65.61 140.9 74.98L197.5 131.6C206.9 140.9 206.9 156.1 197.5 165.5C188.1 174.9 172.9 174.9 163.5 165.5L106.1 108.9C97.61 99.55 97.61 84.35 106.1 74.98V74.98zM469 403.1C478.4 412.4 478.4 427.6 469 437C459.6 446.4 444.5 446.4 435.1 437L378.5 380.4C369.1 371.1 369.1 355.9 378.5 346.5C387.9 337.1 403.1 337.1 412.5 346.5L469 403.1z"></path>
                                        </svg>
                                    </div>
                                    <p className="caption-1 bold text-darwin-accent-bright-blue-800">
                                        Awaiting upload..
                                    </p>
                                </div>
                            )}

                            {!documentFolder.is_processing &&
                                documentFolder.is_smart_report_available && (
                                    <div className="flex items-center space-x-8 bg-darwin-neutral-50 rounded-4 px-8 py-4">
                                        <div className="flex items-center justify-center w-16 h-16">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="15"
                                                height="15"
                                                viewBox="0 0 17 16"
                                                fill="none"
                                            >
                                                <rect
                                                    x="0.876953"
                                                    width="16"
                                                    height="16"
                                                    rx="8"
                                                    fill="#215FFF"
                                                />
                                                <path
                                                    d="M9.32812 3.36328L10.6172 6.13477L13.3887 7.42383C13.5605 7.50977 13.6895 7.68164 13.6895 7.875C13.6895 8.08984 13.5605 8.26172 13.3887 8.34766L10.6172 9.63672L9.32812 12.4082C9.24219 12.5801 9.07031 12.6875 8.87695 12.6875C8.66211 12.6875 8.49023 12.5801 8.4043 12.4082L7.11523 9.63672L4.34375 8.34766C4.17188 8.26172 4.06445 8.08984 4.06445 7.875C4.06445 7.68164 4.17188 7.50977 4.34375 7.42383L7.11523 6.13477L8.4043 3.36328C8.49023 3.19141 8.66211 3.0625 8.87695 3.0625C9.07031 3.0625 9.24219 3.19141 9.32812 3.36328Z"
                                                    fill="white"
                                                />
                                            </svg>
                                        </div>
                                        <p className="caption-1 bold text-darwin-accent-bright-blue-800">
                                            smart
                                        </p>
                                    </div>
                                )}

                            {!documentFolder.is_processing && documentFolder.is_analyzing && (
                                <div className="flex items-center space-x-8 bg-darwin-neutral-50 rounded-4 px-8 py-4">
                                    <div className="flex items-center justify-center w-16 h-16">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 576 512"
                                            className="fill-darwin-accent-bright-blue-800 w-12 h-12 animate-spin"
                                        >
                                            <path d="M312 104C312 117.3 301.3 128 288 128C274.7 128 264 117.3 264 104V24C264 10.75 274.7 0 288 0C301.3 0 312 10.75 312 24V104zM312 488C312 501.3 301.3 512 288 512C274.7 512 264 501.3 264 488V408C264 394.7 274.7 384 288 384C301.3 384 312 394.7 312 408V488zM32 256C32 242.7 42.75 232 56 232H136C149.3 232 160 242.7 160 256C160 269.3 149.3 280 136 280H56C42.75 280 32 269.3 32 256zM520 232C533.3 232 544 242.7 544 256C544 269.3 533.3 280 520 280H440C426.7 280 416 269.3 416 256C416 242.7 426.7 232 440 232H520zM469 74.98C478.4 84.35 478.4 99.55 469 108.9L412.5 165.5C403.1 174.9 387.9 174.9 378.5 165.5C369.1 156.1 369.1 140.9 378.5 131.6L435.1 74.98C444.5 65.61 459.6 65.61 469 74.98zM140.9 437C131.5 446.4 116.4 446.4 106.1 437C97.61 427.6 97.61 412.4 106.1 403.1L163.5 346.5C172.9 337.1 188.1 337.1 197.5 346.5C206.9 355.9 206.9 371.1 197.5 380.4L140.9 437zM106.1 74.98C116.4 65.61 131.5 65.61 140.9 74.98L197.5 131.6C206.9 140.9 206.9 156.1 197.5 165.5C188.1 174.9 172.9 174.9 163.5 165.5L106.1 108.9C97.61 99.55 97.61 84.35 106.1 74.98V74.98zM469 403.1C478.4 412.4 478.4 427.6 469 437C459.6 446.4 444.5 446.4 435.1 437L378.5 380.4C369.1 371.1 369.1 355.9 378.5 346.5C387.9 337.1 403.1 337.1 412.5 346.5L469 403.1z"></path>
                                        </svg>
                                    </div>
                                    <p className="caption-1 bold text-darwin-accent-bright-blue-800">
                                        analysing..
                                    </p>
                                </div>
                            )}
                        </div>
                        {!hideActionCTAs && documentFolder.uploaded_by_me && onDelete && (
                            <div
                                className="absolute top-8 right-12 w-32 h-32 flex items-center justify-center cursor-pointer bg-white rounded-4"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    onDelete();
                                }}
                            >
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 448 512"
                                    className="w-16 h-16 fill-status-error"
                                >
                                    <path d="M424 80C437.3 80 448 90.75 448 104C448 117.3 437.3 128 424 128H412.4L388.4 452.7C385.9 486.1 358.1 512 324.6 512H123.4C89.92 512 62.09 486.1 59.61 452.7L35.56 128H24C10.75 128 0 117.3 0 104C0 90.75 10.75 80 24 80H93.82L130.5 24.94C140.9 9.357 158.4 0 177.1 0H270.9C289.6 0 307.1 9.358 317.5 24.94L354.2 80H424zM177.1 48C174.5 48 171.1 49.34 170.5 51.56L151.5 80H296.5L277.5 51.56C276 49.34 273.5 48 270.9 48H177.1zM364.3 128H83.69L107.5 449.2C108.1 457.5 115.1 464 123.4 464H324.6C332.9 464 339.9 457.5 340.5 449.2L364.3 128z" />
                                </svg>
                            </div>
                        )}
                    </div>
                    <div className={`flex items-start justify-between px-12 py-8 rounded-b-4`}>
                        <div className="space-y-2">
                            <p className="heading-1 bold text-darwin-neutral-1000">
                                {documentFolder.document_date
                                    ? moment(documentFolder.document_date).format('DD')
                                    : '-'}
                            </p>
                            <p className="caption-1 text-darwin-neutral-800">
                                {documentFolder.document_date
                                    ? moment(documentFolder.document_date).format("MMM'YY")
                                    : '-'}
                            </p>
                        </div>
                        <div>
                            <div className="flex items-center space-x-4 bg-darwin-neutral-100 rounded-4 px-9 py-5">
                                {!rtTag && (
                                    <>
                                        {documentFolder.document_type === 'PRESCRIPTION' ? (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 576 512"
                                                className="fill-darwin-neutral-900 w-12 h-12"
                                            >
                                                <path d="M529.1 206.9c-62.38-62.36-163.8-62.49-226.3 0c-62.5 62.49-62.5 163.8 0 226.3c62.5 62.36 163.8 62.49 226.3 0S591.6 269.2 529.1 206.9zM321.8 259.7l154.5 154.5C375.8 478.7 257 360.4 321.8 259.7zM510.3 380.2l-154.5-154.5C456.3 161.1 575 279.5 510.3 380.2zM112 32C50.13 32 0 82.13 0 143.1v223.1c0 61.86 50.13 111.1 112 111.1s112-50.13 112-111.1V143.1C224 82.13 173.9 32 112 32zM176 255.1h-128V143.1c0-84.74 128-84.74 128 0V255.1z"></path>
                                            </svg>
                                        ) : documentFolder.document_type === 'LAB_REPORT' ? (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 512 512"
                                                className="fill-darwin-neutral-900 w-12 h-12"
                                            >
                                                <path d="M504.1 183l-176-176C324.3 2.344 318.1 0 312 0s-12.28 2.344-16.97 7.031c-9.375 9.375-9.375 24.56 0 33.94l15.16 15.16l-277.3 276.5c-38.75 38.75-45.13 102-9.375 143.5C44.08 500 72.76 512 101.5 512h.4473c26.38 0 52.75-9.1 72.88-30.12l281-280.1l15.19 15.19C475.7 221.7 481.8 224 488 224s12.28-2.344 16.97-7.031C514.3 207.6 514.3 192.4 504.1 183zM333.4 256H177.7l166.3-165.9l77.82 77.73L333.4 256z"></path>
                                            </svg>
                                        ) : (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 384 512"
                                                className="fill-darwin-neutral-900 w-12 h-12"
                                            >
                                                <path d="M0 64C0 28.65 28.65 0 64 0H229.5C246.5 0 262.7 6.743 274.7 18.75L365.3 109.3C377.3 121.3 384 137.5 384 154.5V448C384 483.3 355.3 512 320 512H64C28.65 512 0 483.3 0 448V64zM336 448V160H256C238.3 160 224 145.7 224 128V48H64C55.16 48 48 55.16 48 64V448C48 456.8 55.16 464 64 464H320C328.8 464 336 456.8 336 448z"></path>
                                            </svg>
                                        )}
                                    </>
                                )}
                                <p className="text-10 font-700 text-darwin-neutral-900 uppercase">
                                    {rtTag
                                        ? rtTag?.replace(/^RT#/, '')
                                        : OPTIONS.find(
                                              (o) =>
                                                  o.value === documentFolder.latest_document_type,
                                          )?.label}
                                </p>
                            </div>
                            {showPtName && (
                                <p
                                    className="text-12 text-neutral-3"
                                    style={{
                                        // backgroundColor: '#FFF3E7',
                                        padding: 4,
                                        borderRadius: 4,
                                        color: 'rgb(76, 89, 128)',
                                        textAlign: 'end',
                                    }}
                                >
                                    <i> From {documentFolder.patient_name}</i>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default MedicalRecord;
